import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { ContactLine } from "../components/ContactFields.js";
import { Button, inputStyle, Notice, Selector } from "../components/ui.js";
import { useAuth } from "../lib/auth.js";
import {
  fetchAllContacts,
  fetchMembers,
  setMemberCategory,
  setMemberPlayingAs,
  setMemberStatus,
} from "../lib/members.js";
import {
  playingAs,
  type ContactDetails,
  type Member,
  type MemberCategory,
  type MemberRole,
  type MemberStatus,
  type PlayingAs,
} from "../lib/types.js";

const ROLES: MemberRole[] = ["player", "organiser", "admin"];
const PLAYING_AS: PlayingAs[] = ["junior", "adult", "senior"];

/**
 * The membership console. Approvals are the only thing standing between a stranger and
 * everyone's rating, so they are the first thing on the screen and the only place in the
 * app that uses the attention colour for a count.
 */
export function Admin() {
  const { member: me } = useAuth();
  const queryClient = useQueryClient();

  const { data, isPending, error } = useQuery({ queryKey: ["members"], queryFn: fetchMembers });
  // Flat numbers and phone numbers, which an admin may read and a member row does not carry —
  // they are their own table so that nobody else can (20261001000300).
  const { data: contacts } = useQuery({ queryKey: ["all-contacts"], queryFn: fetchAllContacts });
  const contactOf = (m: Member) => contacts?.get(m.id);

  const update = useMutation({
    mutationFn: (input: { id: string; status?: MemberStatus; role?: MemberRole }) =>
      setMemberStatus(input.id, { status: input.status, role: input.role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["members"] }),
  });

  // Separate from `update`: category is not a privilege and does not go through the audited
  // status RPC. See lib/members.ts.
  const recategorise = useMutation({
    mutationFn: (input: { id: string; category: MemberCategory }) =>
      setMemberCategory(input.id, input.category),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["members"] }),
  });

  // Its own RPC and its own audit entry: whether somebody is a junior, an adult or a senior
  // decides which events they may enter, so it is a privilege, not a profile field. One call
  // for both flags, so a junior becomes a senior in one step (20261001000200).
  const regroup = useMutation({
    mutationFn: (input: { id: string; as: PlayingAs }) => setMemberPlayingAs(input.id, input.as),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["members"] }),
  });

  // Sixty-odd members is past the point of scrolling for one name.
  const [search, setSearch] = useState("");
  const needle = search.trim().toLowerCase();
  const matches = (m: Member) => !needle || m.displayName.toLowerCase().includes(needle);

  // Role and suspension used to apply the instant a select changed or a button was pressed —
  // one mis-tap from making somebody an admin, or locking them out, on a phone.
  const confirmRole = (m: Member, role: MemberRole) =>
    window.confirm(
      role === "player"
        ? `Make ${m.displayName} a player again? They lose ${m.role} access.`
        : `Make ${m.displayName} ${role === "admin" ? "an admin" : "an organiser"}?`,
    ) && update.mutate({ id: m.id, role });
  const confirmSuspend = (m: Member) =>
    window.confirm(`Suspend ${m.displayName}? They can't play or sign in until reinstated.`) &&
    update.mutate({ id: m.id, status: "suspended" });

  const members = data ?? [];
  const pending = members.filter((m) => m.status === "pending");
  // Children without a login are listed on their own. They never ask to join, never hold a
  // role, and have no category on purpose — a kids' event is what makes them a kid.
  const allActive = members.filter((m) => m.status === "active" && !m.managed);
  const active = allActive.filter(matches);
  const children = members.filter((m) => m.status === "active" && m.managed && matches(m));
  const inactive = members.filter(
    (m) => (m.status === "suspended" || m.status === "rejected") && matches(m),
  );
  // Members who joined before categories existed. They are missing from the Men and Women
  // boards until someone says which, so the gap is surfaced rather than left to be noticed.
  const uncategorised = allActive.filter((m) => m.category === null);

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <PageHeader eyebrow="Admin" title="Members" />

      {isPending ? <Notice>Loading members…</Notice> : null}
      {error ? <Notice tone="attention">Couldn&rsquo;t load members. Try again.</Notice> : null}
      {update.error ? <Notice tone="attention">{update.error.message}</Notice> : null}
      {recategorise.error ? <Notice tone="attention">{recategorise.error.message}</Notice> : null}
      {regroup.error ? <Notice tone="attention">{regroup.error.message}</Notice> : null}

      {uncategorised.length > 0 ? (
        <Notice tone="attention">
          {uncategorised.length} member{uncategorised.length === 1 ? "" : "s"} without a category.
          They appear on the Everyone board but on neither Men nor Women. Set it below.
        </Notice>
      ) : null}

      <section className="mb-10">
        <div className="micro mb-3 flex items-center gap-2">
          Join requests
          {pending.length > 0 ? (
            <span
              className="font-mono px-1.5 text-[10px]"
              style={{ background: "var(--color-attention-fill)", color: "var(--color-ink)" }}
            >
              {pending.length}
            </span>
          ) : null}
        </div>

        {pending.length === 0 ? (
          <Notice>Nobody waiting. New requests land here.</Notice>
        ) : (
          pending.map((m) => (
            <Row key={m.id} member={m} contact={contactOf(m)}>
              <Button
                tone="primary"
                onClick={() => update.mutate({ id: m.id, status: "active" })}
                disabled={update.isPending}
              >
                Approve
              </Button>
              <Button
                onClick={() => update.mutate({ id: m.id, status: "rejected" })}
                disabled={update.isPending}
              >
                Reject
              </Button>
            </Row>
          ))
        )}
      </section>

      <label className="mb-8 block">
        <span className="field-label">Find a member</span>
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Type a name"
          style={inputStyle}
        />
      </label>

      <section className="mb-10">
        <div className="micro mb-3">
          Active · {needle ? `${active.length} of ${allActive.length}` : allActive.length}
        </div>
        {needle && active.length === 0 ? <Notice>Nobody active by that name.</Notice> : null}
        {active.map((m) => (
          <Row key={m.id} member={m} contact={contactOf(m)}>
            <Selector
              label={`Category for ${m.displayName}`}
              value={m.category ?? ""}
              onChange={(value) =>
                value === ""
                  ? undefined
                  : recategorise.mutate({ id: m.id, category: value as MemberCategory })
              }
              options={[
                ...(m.category ? [] : [{ value: "", label: "Set category" }]),
                { value: "men", label: "Men" },
                { value: "women", label: "Women" },
              ]}
            />
            <Selector
              label={`Role for ${m.displayName}`}
              value={m.role}
              onChange={(value) => confirmRole(m, value as MemberRole)}
              options={ROLES.map((role) => ({
                value: role,
                label: role[0]!.toUpperCase() + role.slice(1),
              }))}
            />
            <Selector
              label={`Playing as, for ${m.displayName}`}
              value={playingAs(m)}
              onChange={(value) => regroup.mutate({ id: m.id, as: value as PlayingAs })}
              options={PLAYING_AS.map((as) => ({
                value: as,
                label: as[0]!.toUpperCase() + as.slice(1),
              }))}
            />
            {m.id === me?.id ? null : (
              <Button onClick={() => confirmSuspend(m)} disabled={update.isPending}>
                Suspend
              </Button>
            )}
          </Row>
        ))}
      </section>

      {children.length > 0 ? (
        <section className="mb-10">
          <div className="micro mb-3">Without a login · {children.length}</div>
          {children.map((m) => (
            <Row key={m.id} member={m}>
              {/* Guardians and the name are managed on the child's own page, where the
                  organiser sees who looks after them before changing it. */}
              <Link to={`/player/${m.id}`}>
                <Button>Guardians</Button>
              </Link>
              <Button onClick={() => confirmSuspend(m)} disabled={update.isPending}>
                Suspend
              </Button>
            </Row>
          ))}
        </section>
      ) : null}

      {inactive.length > 0 ? (
        <section>
          <div className="micro mb-3">Not playing</div>
          {inactive.map((m) => (
            <Row key={m.id} member={m} contact={contactOf(m)}>
              <Button
                onClick={() => update.mutate({ id: m.id, status: "active" })}
                disabled={update.isPending}
              >
                Reinstate
              </Button>
            </Row>
          ))}
        </section>
      ) : null}
    </main>
  );
}

function Row({
  member,
  contact,
  children,
}: {
  member: Member;
  contact?: ContactDetails;
  children: ReactNode;
}) {
  return (
    <article
      className="flex flex-col gap-3 border-t-2 py-3 sm:flex-row sm:items-center"
      style={{ borderColor: "var(--color-line)" }}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate">{member.displayName}</div>
        <div className="micro mt-1">
          {member.status}
          {member.managed ? " · no login" : null}
          {member.junior ? " · junior" : null}
          {member.senior ? " · senior" : null}
          {member.category ? ` · ${member.category}` : null}
          {member.selfAssessed ? ` · self-assessed ${member.selfAssessed}` : null}
        </div>
        {contact ? <ContactLine flatNo={contact.flatNo} phone={contact.phone} /> : null}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </article>
  );
}
